import Session from 'Faster/model/Session';
import SessionService from 'Faster/service/SessionService';
import GeoPoint from 'Faster/model/GeoPoint';

class FasterSession {
  private sessionService: SessionService;

  public constructor(sessionService: SessionService) {
    this.sessionService = sessionService;
  }

  public get(): Session | undefined {
    return this.sessionService.get();
  }

  public putProperty(name: string, value: string | number | boolean): Promise<Session | void> {
    return this.putProperties({
      [name]: value,
    });
  }

  public updateUserId(userId: string): void {
    const session = this.sessionService.get();
    const updatedSession = session.withUpdatedUserId(userId);
    this.sessionService.updateSessionUserId(updatedSession);
  }

  public updateGeoPoint(geoPoint: GeoPoint): void {
    const session = this.sessionService.get();
    const isInTheSameNeighborhood = this.checkSameNeighborhood(geoPoint, session.geoPoint);
    if (!isInTheSameNeighborhood) {
      const updatedSession = session.withUpdatedLocation(geoPoint);
      this.sessionService.updateSessionData(updatedSession);
    }
  }

  public putProperties(properties: object): Promise<void | Session> {
    if (!this.propertiesHaveValidTypes(properties)) {
      console.warn('Unsupported session property, only boolean, number or string are permitted');

      return;
    }

    const session = this.sessionService.get();
    const updatedSession = session.withAddedProperties(properties);

    return this.sessionService.updateSessionData(updatedSession);
  }

  public removeProperty(name: string): void {
    const session = this.sessionService.get();
    const updatedSession = session.withRemovedProperty(name);

    this.sessionService.updateSessionData(updatedSession);
  }

  private propertiesHaveValidTypes(properties: object): boolean {
    const validTypes = ['string', 'boolean', 'number'];
    const propertyArray = Object.values(properties);
    const foundInvalid = propertyArray.some((value): boolean => !validTypes.includes(typeof value));

    return !foundInvalid;
  }

  private checkSameNeighborhood(currentPosition: GeoPoint, previousPosition: GeoPoint): boolean {
    if (previousPosition === null) return false;
    if (previousPosition.latitude === null || previousPosition.longitude === null) return false;
    const isSameLat =
      this.convertToMeters(currentPosition.latitude) === this.convertToMeters(previousPosition.latitude);
    const isSameLong =
      this.convertToMeters(currentPosition.longitude) === this.convertToMeters(previousPosition.longitude);
    return isSameLat && isSameLong;
  }

  private convertToMeters(positionInDegrees: number): number {
    // See https://en.wikipedia.org/wiki/Decimal_degrees
    const decimalDegrees = 3; // neighborhood or street precision ~ 100 meters
    const pow = Math.pow(10, decimalDegrees);
    return Math.round(positionInDegrees * pow);
  }
}

export default FasterSession;
